import { useState } from "react";
import SelectProfile from "../profile/SelectProfile";
import { motion, AnimatePresence } from "framer-motion";
import CustomInput from "../../shared/CustomInput";
import CustomSelect from "../../shared/CustomSelect";
import CustomArea from "../../shared/CustomArea";
import { addSupportTicket } from "../../sdk/support/support";
import Loading from "../../shared/Loading";
import TicketSuccess from "../../components/ticket/TicketSuccess";
import { UserCircle, X } from "lucide-react";

const TopBar = () => {
  const today = new Date()?.toISOString()?.split("T")[0];
  const [showHelp, setShowHelp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [openTicketSuccess, setOpenTicketSuccess] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    impactedPlatform: "",
    deviceOrBrowser: "",
    stepsToReplicate: "",
    expectedResult: "",
    actualResult: "",
    addMoreInfo: "",
    usersImpacted: "",
    memberId: "",
    replicated: "",
    date: "",
    time: "",
    files: null,
  });

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Enter a valid email";
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone Number is required";
    } else if (!/^\+?\d{7,15}$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = "Enter a valid phone number";
    }

    if (!formData.stepsToReplicate.trim()) {
      newErrors.stepsToReplicate = "Steps to replicate are required";
    }
    if (!formData.impactedPlatform.trim()) {
      newErrors.impactedPlatform = "Impacted platform is required";
    }
    if (!formData.deviceOrBrowser.trim()) {
      newErrors.deviceOrBrowser = "Device or browser is required";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({ ...prev, files: e.target.files }));
  };

  const submitSupportTicket = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      const response = await addSupportTicket(
        formData.fullName,
        formData.email,
        formData.phoneNumber,
        formData.impactedPlatform,
        formData.deviceOrBrowser,
        formData.stepsToReplicate,
        formData.expectedResult,
        formData.actualResult,
        formData.addMoreInfo,
        formData.usersImpacted,
        formData.memberId,
        formData.replicated,
        formData.date,
        formData.time,
        formData.files,
      );
      if (response.status === 200 || response.status === 201) {
        setLoading(false);
        setOpenTicketSuccess(true);
        setShowHelp(false);
        return response;
      } else {
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };
  return (
    <>
      {openTicketSuccess && (
        <TicketSuccess closeTicketSuccess={() => setOpenTicketSuccess(false)} />
      )}
      <div className="h-full bg-white w-[100%] flex flex-col pr-[20px]">
        <div className="flex h-full w-[100%] items-center w-full justify-end">
          <div className="flex gap-[20px] items-center">
            <div className="flex items-center gap-[30px]">
              <span className="w-[45px] h-[45px] bg-[#EFEDEC] rounded-[100%] flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="36"
                  height="36"
                  viewBox="0 0 36 36"
                  fill="none"
                >
                  <path
                    d="M6 28.5V25.5H9V15C9 12.925 9.625 11.081 10.875 9.468C12.125 7.855 13.75 6.799 15.75 6.3V5.25C15.75 4.625 15.969 4.0935 16.407 3.6555C16.845 3.2175 17.376 2.999 18 3C18.625 3 19.1565 3.219 19.5945 3.657C20.0325 4.095 20.251 4.626 20.25 5.25V6.3C22.25 6.8 23.875 7.8565 25.125 9.4695C26.375 11.0825 27 12.926 27 15V25.5H30V28.5H6ZM18 33C17.175 33 16.4685 32.706 15.8805 32.118C15.2925 31.53 14.999 30.824 15 30H21C21 30.825 20.706 31.5315 20.118 32.1195C19.53 32.7075 18.824 33.001 18 33ZM12 25.5H24V15C24 13.35 23.4125 11.9375 22.2375 10.7625C21.0625 9.5875 19.65 9 18 9C16.35 9 14.9375 9.5875 13.7625 10.7625C12.5875 11.9375 12 13.35 12 15V25.5Z"
                    fill="#074073"
                  />
                </svg>
              </span>
              <div className="flex items-center justify-center gap-[10px]">
                <UserCircle size={45} color="#074073" />
                <div className="text-[#074073]">
                  <SelectProfile />
                </div>
              </div>
            </div>
            <div
              onClick={() => setShowHelp(true)}
              className="flex items-center gap-[10px] cursor-pointer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="26"
                height="30"
                viewBox="0 0 26 30"
                fill="none"
              >
                <path
                  d="M12.8185 17.2404C11.7459 17.2415 10.6898 16.9358 9.7446 16.3508C8.79941 15.7658 7.9946 14.9196 7.40214 13.8879L8.94914 12.7018C9.37263 13.4387 9.94759 14.0431 10.6227 14.4613C11.2979 14.8794 12.0522 15.0982 12.8185 15.0982C13.5848 15.0982 14.3391 14.8794 15.0143 14.4613C15.6894 14.0431 16.2644 13.4387 16.6879 12.7018L18.2349 13.8879C17.6424 14.9196 16.8376 15.7658 15.8924 16.3508C14.9472 16.9358 13.8911 17.2415 12.8185 17.2404ZM16.5328 5.45467C16.1655 5.45467 15.8064 5.58035 15.501 5.81581C15.1956 6.05127 14.9576 6.38593 14.817 6.77749C14.6764 7.16905 14.6397 7.5999 14.7113 8.01558C14.783 8.43125 14.9599 8.81307 15.2196 9.11275C15.4793 9.41244 15.8102 9.61653 16.1705 9.69921C16.5307 9.78189 16.9041 9.73946 17.2435 9.57727C17.5828 9.41508 17.8729 9.14042 18.0769 8.78803C18.281 8.43564 18.3899 8.02134 18.3899 7.59753C18.3924 7.31533 18.3461 7.03539 18.2536 6.77411C18.1612 6.51283 18.0245 6.27547 17.8515 6.07591C17.6786 5.87636 17.4729 5.71862 17.2464 5.61195C17.02 5.50527 16.7774 5.4518 16.5328 5.45467ZM9.10421 5.45467C8.73691 5.45467 8.37785 5.58035 8.07244 5.81581C7.76704 6.05127 7.529 6.38593 7.38844 6.77749C7.24787 7.16905 7.2111 7.5999 7.28276 8.01558C7.35441 8.43125 7.53129 8.81307 7.79102 9.11275C8.05074 9.41244 8.38165 9.61653 8.7419 9.69921C9.10215 9.78189 9.47556 9.73946 9.81491 9.57727C10.1543 9.41508 10.4443 9.14042 10.6484 8.78803C10.8524 8.43564 10.9614 8.02134 10.9614 7.59753C10.9638 7.31533 10.9175 7.03539 10.825 6.77411C10.7326 6.51283 10.5959 6.27547 10.4229 6.07591C10.25 5.87636 10.0443 5.71862 9.81784 5.61195C9.5914 5.50527 9.34879 5.4518 9.10421 5.45467Z"
                  fill="#074073"
                />
                <path
                  d="M14.612 30L13 28.9286L16.7143 21.4286H22.2857C22.5297 21.429 22.7714 21.3739 22.9968 21.2663C23.2223 21.1588 23.4272 21.001 23.5997 20.8019C23.7723 20.6028 23.909 20.3664 24.0022 20.1063C24.0954 19.8461 24.1432 19.5672 24.1429 19.2857V4.28571C24.1432 4.00419 24.0954 3.72535 24.0022 3.46518C23.909 3.205 23.7723 2.96861 23.5997 2.76954C23.4272 2.57047 23.2223 2.41265 22.9968 2.30511C22.7714 2.19757 22.5297 2.14243 22.2857 2.14286H3.71429C3.4703 2.14243 3.22864 2.19757 3.00315 2.30511C2.77767 2.41265 2.57279 2.57047 2.40027 2.76954C2.22775 2.96861 2.09096 3.205 1.99776 3.46518C1.90456 3.72535 1.85678 4.00419 1.85714 4.28571V19.2857C1.85678 19.5672 1.90456 19.8461 1.99776 20.1063C2.09096 20.3664 2.22775 20.6028 2.40027 20.8019C2.57279 21.001 2.77767 21.1588 3.00315 21.2663C3.22864 21.3739 3.4703 21.429 3.71429 21.4286H12.0714V23.5714H3.71429C2.7292 23.5714 1.78445 23.1199 1.08789 22.3162C0.391326 21.5124 1.1614e-07 20.4224 1.1614e-07 19.2857V4.28571C-0.00012188 3.72287 0.0958676 3.16551 0.282485 2.64548C0.469102 2.12545 0.74269 1.65294 1.08762 1.25494C1.43254 0.856949 1.84205 0.541271 2.29275 0.325944C2.74344 0.110617 3.22648 -0.000140631 3.71429 1.34008e-07H22.2857C22.7735 -0.000140631 23.2566 0.110617 23.7073 0.325944C24.1579 0.541271 24.5675 0.856949 24.9124 1.25494C25.2573 1.65294 25.5309 2.12545 25.7175 2.64548C25.9041 3.16551 26.0001 3.72287 26 4.28571V19.2857C26 20.4224 25.6087 21.5124 24.9121 22.3162C24.2155 23.1199 23.2708 23.5714 22.2857 23.5714H17.7961L14.612 30Z"
                  fill="#074073"
                />
              </svg>
              <div className="text-[#074073] font-normal text-[16px]]">
                <p>Need Help?</p>
              </div>
            </div>
          </div>
        </div>
        {/* Help Modal */}
        <AnimatePresence>
          {showHelp && (
            <>
              {/* Background overlay */}
              <motion.div
                className="fixed inset-0 bg-black/30 z-40"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setShowHelp(false)}
              />

              {/* Sliding modal */}
              <motion.div
                className="fixed right-[0.5%] top-[1%] bottom-[1%] bg-white shadow-lg rounded-2xl z-50 flex flex-col"
                style={{ width: 500 }}
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "spring", stiffness: 100, damping: 20 }}
              >
                <div className="w-full h-full py-6 relative">
                  <div
                    onClick={() => setShowHelp(false)}
                    className="absolute top-[0px] cursor-pointer h-[40px] w-[40px] bg-[#FF0000] rounded-tr-[10px] rounded-bl-[20px] right-[0px] flex items-center justify-center"
                  >
                    <X color="#ffffff" />
                  </div>
                  <div className="h-[10%] w-full px-3">
                    <h2 className="text-lg font-semibold">Contact Support</h2>
                    <p className="text-sm text-[#737373] mb-[15px] text-[15px] font-light">
                      To help us investigate this issue, please provide the
                      below information.
                    </p>
                  </div>
                  <div className="h-[85%] px-6 overflow-y-auto w-full relative">
                    <div className="flex-1 overflow-y-auto">
                      <div>
                        <p className="text-[#000000] mb-[15px] font-medium">
                          General details
                        </p>
                        <div className="w-full mb-[15px] flex items-start justify-between">
                          <div className="w-[49%] flex flex-col items-start">
                            <label
                              htmlFor="name"
                              className="text-[14px] font-medium"
                            >
                              Full Name
                            </label>
                            <CustomInput
                              placeholder="Enter full name"
                              type="text"
                              value={formData.fullName}
                              onChange={(e) =>
                                handleChange("fullName", e.target.value)
                              }
                            />
                            {errors.fullName && (
                              <small className="text-red-500">
                                {errors.fullName}
                              </small>
                            )}
                          </div>
                          <div className="w-[49%] flex flex-col items-start">
                            <label
                              htmlFor="email"
                              className="text-[14px] font-medium"
                            >
                              Email
                            </label>
                            <CustomInput
                              placeholder="Enter email"
                              type="email"
                              value={formData.email}
                              onChange={(e) =>
                                handleChange("email", e.target.value)
                              }
                            />
                            {errors.email && (
                              <small className="text-red-500">
                                {errors.email}
                              </small>
                            )}
                          </div>
                        </div>
                        <div className="w-full mb-[15px] flex flex-col items-start">
                          <label
                            htmlFor="phone"
                            className="text-[14px] font-medium"
                          >
                            Phone Number
                          </label>
                          <CustomInput
                            placeholder="Enter phone number"
                            type="text"
                            value={formData.phoneNumber}
                            onChange={(e) =>
                              handleChange("phoneNumber", e.target.value)
                            }
                          />
                          {errors.phoneNumber && (
                            <small className="text-red-500">
                              {errors.phoneNumber}
                            </small>
                          )}
                        </div>

                        <div>
                          <p className="text-[#000000] mb-[15px] font-medium">
                            Platform details
                          </p>
                          <div className="w-full mb-[20px] flex items-start justify-between">
                            <div className="w-[49%] flex flex-col items-start">
                              <label
                                htmlFor="Impacted Platform"
                                className="text-[14px] font-medium"
                              >
                                Impacted Platform
                              </label>
                              <CustomSelect
                                name="platform"
                                options={[
                                  "Android",
                                  "iOS",
                                  "Web",
                                  "Admin Platform",
                                ]}
                                value={formData.impactedPlatform}
                                onChange={(e) =>
                                  handleChange(
                                    "impactedPlatform",
                                    e.target.value,
                                  )
                                }
                              />
                              {errors.impactedPlatform && (
                                <small className="text-red-500">
                                  {errors.impactedPlatform}
                                </small>
                              )}
                            </div>
                            <div className="w-[49%] flex flex-col items-start">
                              <label
                                htmlFor="Device or Browser"
                                className="text-[14px] font-medium"
                              >
                                Device or Browser affected
                              </label>
                              <CustomInput
                                placeholder="Enter device or browser affected"
                                type="text"
                                value={formData.deviceOrBrowser}
                                onChange={(e) =>
                                  handleChange(
                                    "deviceOrBrowser",
                                    e.target.value,
                                  )
                                }
                              />
                              {errors.deviceOrBrowser && (
                                <small className="text-red-500">
                                  {errors.deviceOrBrowser}
                                </small>
                              )}
                            </div>
                          </div>
                        </div>

                        <div>
                          <p className="text-[#000000] mb-[15px] font-medium">
                            Issues details
                          </p>
                          <div className="w-full mb-[15px] flex flex-col items-start">
                            <label
                              htmlFor="Steps"
                              className="text-[14px] font-medium"
                            >
                              Steps to Replicate
                            </label>
                            <CustomArea
                              placeholder="Enter steps to replicate"
                              value={formData.stepsToReplicate}
                              onChange={(e) =>
                                handleChange("stepsToReplicate", e.target.value)
                              }
                            />
                            {errors.stepsToReplicate && (
                              <small className="text-red-500">
                                {errors.stepsToReplicate}
                              </small>
                            )}
                          </div>
                        </div>

                        <div className="w-full mb-[15px] flex flex-col items-start">
                          <label
                            htmlFor="Expected"
                            className="text-[14px] font-medium"
                          >
                            Expected Result
                          </label>
                          <CustomArea
                            placeholder="Enter expected result"
                            value={formData.expectedResult}
                            onChange={(e) =>
                              handleChange("expectedResult", e.target.value)
                            }
                          />
                        </div>

                        <div className="w-full mb-[15px] flex flex-col items-start">
                          <label
                            htmlFor="Actual"
                            className="text-[14px] font-medium"
                          >
                            Actual Result
                          </label>
                          <CustomArea
                            placeholder="Enter actual result"
                            value={formData.actualResult}
                            onChange={(e) =>
                              handleChange("actualResult", e.target.value)
                            }
                          />
                        </div>

                        <div className="w-full mb-[15px] flex flex-col items-start">
                          <label
                            htmlFor="Add more information"
                            className="text-[14px] font-medium"
                          >
                            Add more information
                          </label>
                          <CustomArea
                            placeholder="Add any other relevant information"
                            value={formData.addMoreInfo}
                            onChange={(e) =>
                              handleChange("addMoreInfo", e.target.value)
                            }
                          />
                        </div>

                        <div className="w-full mb-[15px] flex flex-col items-start">
                          <label
                            htmlFor="users"
                            className="text-[14px] font-medium"
                          >
                            How many users were impacted?
                          </label>
                          <CustomInput
                            placeholder="Enter number of users"
                            type="text"
                            value={formData.usersImpacted}
                            onChange={(e) =>
                              handleChange("usersImpacted", e.target.value)
                            }
                          />
                        </div>

                        <div className="w-full mb-[15px] flex flex-col items-start">
                          <label
                            htmlFor="member"
                            className="text-[14px] font-medium"
                          >
                            What is the member ID?
                          </label>
                          <CustomInput
                            placeholder="Enter member"
                            type="text"
                            value={formData.memberId}
                            onChange={(e) =>
                              handleChange("memberId", e.target.value)
                            }
                          />
                        </div>

                        <div className="w-full mb-[15px] flex flex-col items-start">
                          <label
                            htmlFor="replicate"
                            className="text-[14px] font-medium"
                          >
                            Were you able to replicate the issue?
                          </label>
                          <CustomSelect
                            placeholder="Were you able to replicate the issue?"
                            options={["Yes", "No"]}
                            value={formData.replicated}
                            onChange={(e) =>
                              handleChange("replicated", e.target.value)
                            }
                          />
                        </div>

                        <div className="w-full mb-[15px] flex items-start justify-between">
                          <div className="w-[49%] flex flex-col items-start">
                            <label
                              htmlFor="date"
                              className="text-[14px] font-medium"
                            >
                              Date
                            </label>
                            <input
                              className="h-[45px] w-full text-[13px] rounded-[2px] px-[10px] border border-[#D9D9D9] focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-[#D9D9D9]"
                              type="date"
                              value={formData?.date}
                              onChange={(e) =>
                                handleChange("date", e.target.value)
                              }
                              max={today}
                            />
                          </div>
                          <div className="w-[49%] flex flex-col items-start">
                            <label
                              htmlFor="time"
                              className="text-[14px] font-medium"
                            >
                              Time
                            </label>
                            <CustomInput
                              type="time"
                              value={formData.time}
                              onChange={(e) =>
                                handleChange("time", e.target.value)
                              }
                            />
                          </div>
                        </div>

                        <div className="mb-[15px] w-full">
                          <p className="text-[14px] font-medium">
                            Add a screenshot or file
                          </p>
                          <div className="w-full h-[130px] bg-[#EAEAEA] p-[15px] rounded flex flex-col items-center justify-center">
                            <input
                              type="file"
                              multiple
                              onChange={handleFileChange}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="h-[5%] w-full px-3">
                    <button
                      disabled={loading}
                      onClick={submitSupportTicket}
                      className="bg-[#074073] text-white text-[15px] font-bold rounded-sm w-full h-[40px] flex gap-[5px] items-center justify-center"
                    >
                      {loading && <Loading />}
                      Submit
                    </button>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default TopBar;
